import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { FaCheck, FaPlus, FaTimes } from 'react-icons/fa';
import { MdPayment } from 'react-icons/md';
import { IconWrapper } from '../../components/IconWrapper';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import { listProjectActivityEvents, type ActivityEventRow } from '../../services/activityEvents.service';
import {
  defaultCompatiblePattern,
  editedScheduleIsReconciled,
  issuedDepositLock,
  issuedStageTotal,
  money,
  patternChoiceLabel,
  presentScheduleStages,
  proposeRetrospectiveMapping,
  remainingToInvoice,
  schedulePatternChoices,
  stageKindForIndex,
} from './paymentSchedule';
import { paymentScheduleService } from './paymentSchedule.service';
import type {
  PaymentSchedule,
  PlannedStageDraft,
  PresentedScheduleStage,
  RetrospectiveMappingRow,
  ScheduleInvoice,
} from './paymentSchedule.types';

function formatMoney(value?: number | null) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value));
}

function formatWhen(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusColor(status: PresentedScheduleStage['status'] | 'review'): 'success' | 'primary' | 'neutral' | 'warning' {
  if (status === 'issued') return 'success';
  if (status === 'ready') return 'primary';
  if (status === 'review') return 'warning';
  return 'neutral';
}

export function PaymentScheduleCard({
  projectId,
  schedule,
  grossTotal,
  invoices,
  depositAmount,
  depositPercentage,
  quotedPattern,
  finalSettled,
  firstAccepted,
  onChanged,
}: {
  projectId: string;
  schedule: PaymentSchedule | null;
  grossTotal: number;
  invoices: ScheduleInvoice[];
  depositAmount?: number | null;
  depositPercentage?: number | null;
  quotedPattern?: string | null;
  finalSettled: boolean;
  firstAccepted?: boolean;
  onChanged: () => void;
}) {
  const presented = useMemo(
    () => (schedule
      ? presentScheduleStages({
        stages: schedule.stages,
        grossTotal,
        invoices,
        finalSettled,
        firstAccepted,
      })
      : []),
    [schedule, grossTotal, invoices, finalSettled, firstAccepted],
  );
  const invoiced = issuedStageTotal(schedule?.stages || []);
  const remaining = remainingToInvoice({ grossTotal, invoices });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<ActivityEventRow[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!historyOpen || !schedule) return;
    let alive = true;
    void listProjectActivityEvents(projectId, 80).then((rows) => {
      if (!alive) return;
      setHistory(rows.filter((row) => String(row.action || '').startsWith('payment_schedule.')));
    });
    return () => { alive = false; };
  }, [historyOpen, projectId, schedule]);

  if (!schedule) {
    return (
      <Card variant="outlined">
        <CardContent>
          <WorkspaceCardHeader
            icon={MdPayment}
            title="Payment Schedule"
            description="Optional staged invoicing. Ordinary Deposit → Balance jobs can leave this alone."
            action={(
              <Button size="sm" variant="outlined" onClick={() => setAddOpen(true)}>
                Add Payment Schedule
              </Button>
            )}
          />
          <AddScheduleDialog
            open={addOpen}
            busy={busy}
            error={error}
            invoices={invoices}
            grossTotal={grossTotal}
            depositAmount={depositAmount}
            depositPercentage={depositPercentage}
            quotedPattern={quotedPattern}
            onClose={() => { setAddOpen(false); setError(null); }}
            onConfirm={async (pattern, drafts) => {
              setBusy(true);
              setError(null);
              try {
                await paymentScheduleService.createRetrospective({
                  projectId,
                  pattern,
                  grossTotal,
                  invoices,
                  depositAmount,
                  depositPercentage,
                  drafts,
                });
                setAddOpen(false);
                onChanged();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to add Payment Schedule');
              } finally {
                setBusy(false);
              }
            }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdPayment}
          title="Payment Schedule"
          description="Future invoices follow these milestones. Issued invoices stay frozen."
          action={(
            <Button size="sm" variant="outlined" onClick={() => setEditOpen(true)}>
              Edit Payment Schedule
            </Button>
          )}
        />
        {schedule.needsReview && (
          <Alert color="warning" sx={{ mb: 1.5 }}>
            Payment schedule needs reviewing
          </Alert>
        )}
        <MilestoneChart stages={presented} needsReview={schedule.needsReview} />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={2}
          sx={{ mt: 1.5, mb: 1 }}
          flexWrap="wrap"
        >
          <ScheduleStat label="Agreed project value" value={formatMoney(grossTotal)} />
          <ScheduleStat label="Total invoiced" value={formatMoney(invoiced)} />
          <ScheduleStat label="Remaining to invoice" value={formatMoney(remaining)} />
        </Stack>
        <AccordionGroup sx={{ mt: 0.5 }}>
          <Accordion expanded={historyOpen} onChange={(_, next) => setHistoryOpen(Boolean(next))}>
            <AccordionSummary>
              <Typography level="title-sm">Payment Schedule History</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {history.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No schedule history yet.</Typography>
              ) : (
                <Stack gap={1}>
                  {history.map((row) => (
                    <Box key={row.id}>
                      <Typography level="body-sm">{row.label}</Typography>
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {formatWhen(row.occurredAt)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </AccordionGroup>
        <EditScheduleDialog
          open={editOpen}
          schedule={schedule}
          grossTotal={grossTotal}
          busy={busy}
          error={error}
          onClose={() => { setEditOpen(false); setError(null); }}
          onRemove={async () => {
            setBusy(true);
            setError(null);
            try {
              await paymentScheduleService.remove({ schedule, grossTotal, invoices });
              setEditOpen(false);
              onChanged();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to remove Payment Schedule');
            } finally {
              setBusy(false);
            }
          }}
          onSave={async (drafts) => {
            setBusy(true);
            setError(null);
            try {
              if (!editedScheduleIsReconciled({
                issuedTotal: issuedStageTotal(schedule.stages),
                futurePlannedTotal: drafts
                  .filter((draft) => draft.status !== 'issued')
                  .reduce((sum, draft) => sum + money(draft.plannedAmount), 0),
                grossTotal,
              })) {
                throw new Error('Issued total plus future planned amounts must equal the current agreed project value.');
              }
              await paymentScheduleService.saveEdits({
                schedule,
                drafts,
                grossTotal,
                invoices,
              });
              setEditOpen(false);
              onChanged();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to save Payment Schedule');
            } finally {
              setBusy(false);
            }
          }}
        />
      </CardContent>
    </Card>
  );
}

function ScheduleStat({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{label}</Typography>
      <Typography level="title-sm">{value}</Typography>
    </Box>
  );
}

function MilestoneChart({
  stages,
  needsReview,
}: {
  stages: PresentedScheduleStage[];
  needsReview: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="stretch"
      gap={0.5}
      sx={{ overflowX: 'auto', py: 0.5 }}
    >
      {stages.map((stage, index) => (
        <React.Fragment key={stage.id}>
          <Box sx={{ minWidth: 104, textAlign: 'center', px: 0.5 }}>
            <Chip
              size="sm"
              variant="soft"
              color={needsReview && !stage.locked ? 'warning' : statusColor(stage.status)}
              startDecorator={stage.status === 'issued' ? <IconWrapper icon={FaCheck} /> : undefined}
            >
              {stage.label}
            </Chip>
            <Typography level="body-sm" sx={{ mt: 0.75, fontWeight: 600 }}>
              {formatMoney(stage.amount)}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              {stage.amountCaption}
            </Typography>
          </Box>
          {index < stages.length - 1 && (
            <Box
              sx={{
                flex: 1,
                minWidth: 16,
                alignSelf: 'center',
                height: 2,
                bgcolor: stage.status === 'issued' ? 'success.300' : 'neutral.200',
                mb: 3,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Stack>
  );
}

function AddScheduleDialog({
  open,
  busy,
  error,
  invoices,
  grossTotal,
  depositAmount,
  depositPercentage,
  quotedPattern,
  onClose,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  invoices: ScheduleInvoice[];
  grossTotal: number;
  depositAmount?: number | null;
  depositPercentage?: number | null;
  quotedPattern?: string | null;
  onClose: () => void;
  onConfirm: (pattern: string, drafts: PlannedStageDraft[]) => Promise<void>;
}) {
  const lock = useMemo(
    () => issuedDepositLock({ invoices, grossTotal, depositPercentage }),
    [invoices, grossTotal, depositPercentage],
  );
  const choices = useMemo(
    () => schedulePatternChoices({ invoices, grossTotal, depositPercentage }),
    [invoices, grossTotal, depositPercentage],
  );
  const [pattern, setPattern] = useState(() => defaultCompatiblePattern({ quotedPattern, choices }));
  useEffect(() => {
    if (!open) return;
    setPattern(defaultCompatiblePattern({ quotedPattern, choices }));
  }, [open, quotedPattern, choices]);
  const lockedDepositAmount = lock?.amount ?? money(depositAmount);
  const proposed = useMemo(
    () => proposeRetrospectiveMapping({
      pattern,
      grossTotal,
      invoices,
      depositAmount: lockedDepositAmount > 0 ? lockedDepositAmount : depositAmount,
    }),
    [pattern, grossTotal, invoices, lockedDepositAmount, depositAmount],
  );
  const [rows, setRows] = useState<RetrospectiveMappingRow[]>(proposed.rows);
  useEffect(() => { setRows(proposed.rows); }, [proposed.rows]);
  const hasHistoricalWorks = proposed.rows.some((row) => row.issued && row.invoiceRole === 'works');
  const remaining = remainingToInvoice({ grossTotal, invoices });
  const lastIndex = rows.length - 1;
  const finalIsFuture = rows.length > 0 && !rows[lastIndex].issued;
  // Stages to come take typed amounts; the last one is the Final Balance and takes what is left.
  const beforeFinalTotal = money(rows.reduce((sum, row, index) => (
    !row.issued && index < lastIndex ? sum + money(row.invoiceAmount) : sum
  ), 0));
  const finalAmount = money(remaining - beforeFinalTotal);
  const customised = rows.length !== proposed.rows.length || rows.some((row, index) => (
    !row.issued && index < lastIndex && money(row.invoiceAmount) !== money(proposed.rows[index]?.invoiceAmount)
  ));
  const updateRow = (index: number, patch: Partial<RetrospectiveMappingRow>) => {
    setRows(rows.map((row, itemIndex) => (itemIndex === index ? { ...row, ...patch } : row)));
  };
  const addStage = () => {
    const insertAt = finalIsFuture ? lastIndex : rows.length;
    const next = [...rows];
    next.splice(insertAt, 0, {
      invoiceId: null,
      invoiceRole: null,
      invoiceAmount: 0,
      proposedLabel: 'Progress',
      proposedKind: 'interim',
      issued: false,
    });
    setRows(next);
  };
  const buildDrafts = (): PlannedStageDraft[] => rows.map((row, index) => {
    const kind = stageKindForIndex(index, rows.length);
    const proposedDraft = row.issued
      ? proposed.drafts.find((draft) => draft.invoiceId === row.invoiceId) || proposed.drafts[index]
      : (customised ? null : proposed.drafts[index]);
    const label = String(row.proposedLabel || '').trim() || proposedDraft?.label || 'Stage';
    if (row.issued && proposedDraft) return { ...proposedDraft, sortOrder: index, kind, label };
    return {
      sortOrder: index,
      label,
      kind,
      // Typed amounts replace the pattern's proportions, so later re-splits weigh by amount.
      plannedPercent: proposedDraft?.plannedPercent ?? null,
      plannedAmount: index === lastIndex ? finalAmount : money(row.invoiceAmount),
      status: 'expected',
    };
  });
  // From the invoiced amount, not the quoted percentage: a corrected deposit no longer matches it.
  const depositPercentLabel = lock && grossTotal > 0
    ? ` (${Math.round((lock.amount / grossTotal) * 1000) / 10}%)`
    : '';

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 560, width: '100%' }}>
        <ModalClose />
        <DialogTitle>Add Payment Schedule</DialogTitle>
        <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
          Existing issued invoices stay exactly as they are. Confirm how those invoices map onto the new milestones,
          and set the amounts for the stages still to come. The Final Balance takes what is left.
        </Typography>
        {lock && (
          <Alert color="neutral" sx={{ mt: 1.5 }}>
            A deposit of {formatMoney(lock.amount)}{depositPercentLabel} has already been invoiced and stays as it is. Later stages split what is left in the pattern's proportions.
          </Alert>
        )}
        <FormControl sx={{ mt: 1.5 }}>
          <FormLabel>Pattern</FormLabel>
          <Select value={pattern} onChange={(_, value) => value && setPattern(value)}>
            {choices.map((item) => (
              <Option key={item.label} value={item.label}>{patternChoiceLabel(item.label)}</Option>
            ))}
          </Select>
        </FormControl>
        <Stack gap={1} sx={{ mt: 1.5 }}>
          {rows.map((row, index) => (
            <Stack key={`${row.invoiceId || 'future'}-${index}`} direction="row" gap={1} alignItems="center">
              <Chip size="sm" variant="soft" color={row.issued ? 'success' : 'neutral'}>
                {row.issued ? 'Issued' : 'Expected'}
              </Chip>
              <Input
                size="sm"
                value={row.proposedLabel}
                disabled={row.proposedKind === 'deposit' && row.issued}
                onChange={(event) => updateRow(index, { proposedLabel: event.target.value })}
                sx={{ flex: 1 }}
              />
              {row.issued ? (
                <Typography level="body-sm" sx={{ width: 140, textAlign: 'right' }}>
                  {formatMoney(row.invoiceAmount)}
                </Typography>
              ) : index === lastIndex ? (
                <Input
                  size="sm"
                  readOnly
                  value={finalAmount.toFixed(2)}
                  startDecorator="£"
                  color={finalAmount < 0 ? 'danger' : 'neutral'}
                  title="Whatever is left after the stages above"
                  sx={{ width: 140, bgcolor: 'background.level1' }}
                />
              ) : (
                <Input
                  size="sm"
                  type="number"
                  value={String(row.invoiceAmount)}
                  onChange={(event) => updateRow(index, { invoiceAmount: money(event.target.value) })}
                  startDecorator="£"
                  sx={{ width: 140 }}
                />
              )}
              {!row.issued && index !== lastIndex ? (
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  aria-label={`Remove ${row.proposedLabel}`}
                  onClick={() => setRows(rows.filter((_, itemIndex) => itemIndex !== index))}
                >
                  <IconWrapper icon={FaTimes} />
                </IconButton>
              ) : (
                <Box sx={{ width: 32 }} />
              )}
            </Stack>
          ))}
        </Stack>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<IconWrapper icon={FaPlus} />}
          sx={{ mt: 1, alignSelf: 'flex-start' }}
          onClick={addStage}
        >
          Add stage
        </Button>
        {finalIsFuture && (
          <Typography level="body-sm" sx={{ mt: 1.25, color: finalAmount < 0 ? 'warning.700' : 'success.600' }}>
            {finalAmount < 0
              ? `Stages before the Final Balance are ${formatMoney(-finalAmount)} over the ${formatMoney(remaining)} left to invoice`
              : `Final Balance takes what is left · remaining ${formatMoney(remaining)}`}
          </Typography>
        )}
        {hasHistoricalWorks && (
          <Alert color="neutral" sx={{ mt: 1.5 }}>
            Works invoices are not labelled from sequence automatically. Confirm the historical mapping before creating the schedule.
          </Alert>
        )}
        {error && <Alert color="danger" sx={{ mt: 1 }}>{error}</Alert>}
        <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
          <Button variant="plain" color="neutral" onClick={onClose}>Cancel</Button>
          <Button
            loading={busy}
            disabled={finalIsFuture && finalAmount < 0}
            onClick={() => { void onConfirm(pattern, buildDrafts()); }}
          >
            Create schedule
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}

function EditScheduleDialog({
  open,
  schedule,
  grossTotal,
  busy,
  error,
  onClose,
  onRemove,
  onSave,
}: {
  open: boolean;
  schedule: PaymentSchedule;
  grossTotal: number;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onRemove: () => Promise<void>;
  onSave: (drafts: PlannedStageDraft[]) => Promise<void>;
}) {
  const [drafts, setDrafts] = useState<PlannedStageDraft[]>([]);
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => {
    if (!open) return;
    setConfirmRemove(false);
    setDrafts(schedule.stages.map((stage) => ({
      sortOrder: stage.sortOrder,
      label: stage.label,
      kind: stage.kind,
      plannedPercent: stage.plannedPercent,
      plannedAmount: stage.status === 'issued' ? money(stage.issuedAmount) : stage.plannedAmount,
      invoiceId: stage.invoiceId,
      issuedAmount: stage.issuedAmount,
      status: stage.status,
    })));
  }, [open, schedule]);

  const issued = drafts.filter((draft) => draft.status === 'issued');
  const typedFuture = drafts.filter((draft) => draft.status !== 'issued');
  const remaining = money(Math.max(grossTotal - issuedStageTotal(schedule.stages), 0));
  // The last stage is always the Final Balance: whatever the stages before it leave.
  const beforeFinalTotal = money(typedFuture
    .slice(0, -1)
    .reduce((sum, draft) => sum + money(draft.plannedAmount), 0));
  const finalAmount = money(remaining - beforeFinalTotal);
  const future = typedFuture.map((draft, index) => (
    index === typedFuture.length - 1 ? { ...draft, plannedAmount: finalAmount } : draft
  ));
  const futureTotal = future.reduce((sum, draft) => sum + money(draft.plannedAmount), 0);
  const reconciled = finalAmount >= 0 && editedScheduleIsReconciled({
    issuedTotal: issuedStageTotal(schedule.stages),
    futurePlannedTotal: futureTotal,
    grossTotal,
  });

  const updateFuture = (index: number, patch: Partial<PlannedStageDraft>) => {
    const nextFuture = future.map((draft, itemIndex) => (
      itemIndex === index ? { ...draft, ...patch } : draft
    ));
    setDrafts([
      ...issued,
      ...nextFuture.map((draft, itemIndex, list) => ({
        ...draft,
        sortOrder: issued.length + itemIndex,
        kind: stageKindForIndex(issued.length + itemIndex, issued.length + list.length),
      })),
    ]);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 560, width: '100%' }}>
        <ModalClose />
        <DialogTitle>Edit Payment Schedule</DialogTitle>
        <Alert color="neutral">
          Changes to the Payment Schedule affect future invoices only. Previously issued invoices will not be changed.
        </Alert>
        <Stack gap={1} sx={{ mt: 1.5 }}>
          {issued.map((draft) => (
            <Stack key={draft.invoiceId || draft.label} direction="row" gap={1} alignItems="center">
              <Chip size="sm" color="success" variant="soft">Issued</Chip>
              <Typography level="body-sm" sx={{ flex: 1 }}>{draft.label}</Typography>
              <Typography level="body-sm">{formatMoney(draft.issuedAmount)}</Typography>
            </Stack>
          ))}
          {future.map((draft, index) => (
            <Stack key={`future-${index}`} direction="row" gap={1} alignItems="center">
              <Input
                size="sm"
                value={draft.label}
                onChange={(event) => updateFuture(index, { label: event.target.value })}
                sx={{ flex: 1 }}
              />
              {index === future.length - 1 ? (
                <Input
                  size="sm"
                  readOnly
                  value={finalAmount.toFixed(2)}
                  startDecorator="£"
                  color={finalAmount < 0 ? 'danger' : 'neutral'}
                  title="Whatever is left after the stages above"
                  sx={{ width: 140, bgcolor: 'background.level1' }}
                />
              ) : (
                <Input
                  size="sm"
                  type="number"
                  value={String(draft.plannedAmount)}
                  onChange={(event) => updateFuture(index, { plannedAmount: money(event.target.value) })}
                  startDecorator="£"
                  sx={{ width: 140 }}
                />
              )}
              {future.length > 1 && (
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  aria-label={`Remove ${draft.label}`}
                  onClick={() => {
                    const nextFuture = future.filter((_, itemIndex) => itemIndex !== index);
                    setDrafts([
                      ...issued,
                      ...nextFuture.map((item, itemIndex, list) => ({
                        ...item,
                        sortOrder: issued.length + itemIndex,
                        kind: stageKindForIndex(issued.length + itemIndex, issued.length + list.length),
                      })),
                    ]);
                  }}
                >
                  <IconWrapper icon={FaTimes} />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<IconWrapper icon={FaPlus} />}
          sx={{ mt: 1, alignSelf: 'flex-start' }}
          onClick={() => {
            const insertAt = Math.max(future.length - 1, 0);
            const nextFuture = [...future];
            nextFuture.splice(insertAt, 0, {
              sortOrder: issued.length + insertAt,
              label: 'Progress',
              kind: 'interim',
              plannedPercent: null,
              plannedAmount: 0,
              status: 'expected',
            });
            setDrafts([
              ...issued,
              ...nextFuture.map((item, itemIndex, list) => ({
                ...item,
                sortOrder: issued.length + itemIndex,
                kind: stageKindForIndex(issued.length + itemIndex, issued.length + list.length),
              })),
            ]);
          }}
        >
          Add future stage
        </Button>
        <Typography level="body-sm" sx={{ mt: 1.25, color: reconciled ? 'success.600' : 'warning.700' }}>
          {finalAmount < 0
            ? `Stages before the Final Balance are ${formatMoney(-finalAmount)} over the ${formatMoney(remaining)} left to invoice`
            : `Final Balance takes what is left · remaining ${formatMoney(remaining)}`}
        </Typography>
        {error && <Alert color="danger" sx={{ mt: 1 }}>{error}</Alert>}
        {confirmRemove && (
          <Alert
            color="warning"
            sx={{ mt: 1.5, alignItems: 'flex-start' }}
            endDecorator={(
              <Stack direction="row" gap={1}>
                <Button size="sm" variant="plain" color="neutral" onClick={() => setConfirmRemove(false)}>Keep</Button>
                <Button size="sm" color="danger" loading={busy} onClick={() => { void onRemove(); }}>Remove</Button>
              </Stack>
            )}
          >
            Remove this Payment Schedule? Invoices are not changed. Add a new schedule afterwards and the
            existing invoices are mapped onto it. If the quote has a payment structure, the schedule is
            rebuilt from the quote.
          </Alert>
        )}
        <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
          <Button
            variant="plain"
            color="danger"
            sx={{ mr: 'auto' }}
            disabled={busy || confirmRemove}
            onClick={() => setConfirmRemove(true)}
          >
            Remove schedule
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>Cancel</Button>
          <Button
            loading={busy}
            disabled={finalAmount < 0}
            onClick={() => { void onSave([...issued, ...future]); }}
          >
            Save schedule
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
